import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';

import { HierarchyCountBadge } from './HierarchyCountBadge';
import { filterHierarchyTree, isHierarchyFilterActive } from './hierarchyFilter';
import { buildTotalCountsById } from './HierarchyTree';
import { labelSx } from './LocationHelpers';
import { StructureHierarchyTable } from './StructureHierarchyTable';
import { ServiceLocationHierarchy } from '../../../../../@types/service.type';
import { MultiSelectDropdown } from '../../../../../components/molecules/FilterBar';
import { STANDARD_FIELD_HEIGHT } from '../../../../../configs/ui-constants';

// "Jobs" and "Parts" are independently tickable — ticking both requires a
// row to have BOTH a job and a part itself, not one of each spread across
// different rows. Genuinely multi-select (0, 1, or 2 at once), which is
// what MultiSelectDropdown is for — a single-choice SelectMenu wouldn't fit.
const WITH_FILTER_OPTIONS = [
  { label: 'Jobs', value: 'jobs' },
  { label: 'Parts', value: 'parts' },
];

export interface StructureHierarchySectionProps {
  /** The job's own location — callers should resolve this with
   *  HierarchyTree.findOwnLocationNode, not pass the raw fetched hierarchy
   *  payload; that payload's top node can sit above the job's actual
   *  location. See the component doc comment below. */
  hierarchy: ServiceLocationHierarchy | null;
  /** The specific terminal/device this job actually refers to, if any —
   *  highlighted wherever it appears as a row. Not the same node as
   *  `hierarchy` (which is the location, never itself a row). */
  activeNodeId?: number;
  /** Gates the table's Name-column links to `/structure/:id` — same
   *  permission check the "Location" field above already uses. */
  canViewStructure?: boolean;
}

/**
 * The "Structure Hierarchy" section on a job's Location panel. A hierarchy
 * can run to thousands of nodes (a national client's full site/building/room
 * tree), so:
 * - `hierarchy` — the job's own location/outlet, the same node already
 *   shown in the "Location" field above — is shown as a header line with
 *   its own totals, not as a table row. Repeating that name as a row would
 *   just duplicate the "Location" field for no benefit, and it's never a
 *   click target. Only its children — the terminals/devices at that
 *   location — become table rows. A sibling location's jobs are never
 *   relevant here, so the caller must anchor `hierarchy` at this job's own
 *   location, not at whatever the fetched payload's top node happens to be
 *   (which can sit above it, e.g. at a brand with other sibling outlets).
 * - the flattened row list pages through the table below rather than
 *   mounting everything at once, and can be sorted by Jobs/Parts;
 * - search and a "Jobs"/"Parts" filter (Jobs on by default — a location's
 *   terminal list is usually only interesting for the ones with jobs)
 *   prune to matches plus their ancestors (hierarchyFilter.filterHierarchyTree);
 * - job/part count badges always show a location's true total, computed
 *   once from the unfiltered hierarchy (HierarchyTree.buildTotalCountsById),
 *   regardless of what search/filter currently hides underneath it. The
 *   header's own *jobs* badge is the one exception — it uses the root's raw
 *   `serviceCount`, not buildTotalCountsById's self+descendant sum, to avoid
 *   double-counting (see the comment at its computation below).
 *
 * The underlying `service.locationHierarchy` payload is fetched whole in one
 * shot with no backend pagination — this only fixes what renders, not what's
 * downloaded or queried. See the Backlog entry in CLAUDE.local.md.
 */
export const StructureHierarchySection: React.FC<StructureHierarchySectionProps> = ({
  hierarchy,
  activeNodeId,
  canViewStructure = false,
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [withFilter, setWithFilter] = useState<string[]>(['jobs']);
  const requireJobs = withFilter.includes('jobs');
  const requireParts = withFilter.includes('parts');
  const isFiltering = isHierarchyFilterActive({ searchTerm, requireJobs, requireParts });

  // Matches the search-box convention used on the other search pages'
  // filter bars (see FilterBar.tsx's inputSx) — a slim bordered InputBase
  // at the standard field height, not a full MUI TextField.
  const searchInputSx = {
    border: `1px solid ${theme.palette.v4.filterInputBorder}`,
    borderRadius: 1,
    px: 1,
    height: STANDARD_FIELD_HEIGHT,
    boxSizing: 'border-box' as const,
    fontSize: '0.75rem',
    bgcolor: theme.palette.v4.surface,
    '& input': { p: 0 },
  };

  // Always built from the original hierarchy, never from the filtered
  // view — see the doc comment above.
  const totalsById = useMemo(() => buildTotalCountsById(hierarchy), [hierarchy]);
  // The root's *jobs* total is its own `serviceCount` alone, not
  // self+descendants like buildTotalCountsById computes for every other
  // node. Every job at this location carries the root's own id as its
  // `mainLocationNodeId`, so the backend's per-node serviceCount for the
  // root already totals every job at every descendant terminal (each job
  // is matched a second time there, via its own `subLocationNodeId`).
  // Adding the descendant sum on top would double it. Parts have no such
  // duplication — a part is filed at exactly one node — so the true
  // self+descendant total from buildTotalCountsById is still correct there.
  const rootTotals = hierarchy
    ? { services: hierarchy.serviceCount ?? 0, parts: totalsById.get(hierarchy.id)?.parts ?? 0 }
    : null;
  const rootChildren = useMemo(() => hierarchy?.children ?? [], [hierarchy]);
  const displayedNodes = useMemo(() => {
    if (!isFiltering) return rootChildren;
    return rootChildren
      .map((child) => filterHierarchyTree(child, { searchTerm, requireJobs, requireParts }))
      .filter((child): child is ServiceLocationHierarchy => child !== null);
  }, [rootChildren, isFiltering, searchTerm, requireJobs, requireParts]);

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', mt: 2, pt: 2 }}>
      <Typography sx={{ ...labelSx, mb: 1 }}>Structure Hierarchy</Typography>
      {hierarchy && rootTotals && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>{hierarchy.name}</Typography>
          <HierarchyCountBadge
            nodeId={hierarchy.id}
            count={rootTotals.services}
            ownCount={hierarchy.serviceCount ?? 0}
            kind="services"
          />
          <HierarchyCountBadge
            nodeId={hierarchy.id}
            count={rootTotals.parts}
            ownCount={hierarchy.partCount ?? 0}
            kind="parts"
          />
        </Box>
      )}
      {rootChildren.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {/* MultiSelectDropdown doesn't expose a width/sx prop — its trigger
              button has a hardcoded minWidth of 120px. Overriding it from
              here (rather than changing the shared component's default for
              every other caller) via its own MUI class. */}
          <Box sx={{ '& .MuiButton-root': { minWidth: 90 } }}>
            <MultiSelectDropdown
              label="With"
              options={WITH_FILTER_OPTIONS}
              selected={withFilter}
              onChange={setWithFilter}
            />
          </Box>
          <InputBase
            placeholder="Search hierarchy..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{ 'aria-label': 'Search structure hierarchy' }}
            sx={{ ...searchInputSx, width: 180 }}
          />
        </Box>
      )}
      {rootChildren.length === 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          No hierarchy data
        </Typography>
      )}
      {rootChildren.length > 0 && isFiltering && displayedNodes.length === 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          No matching locations
        </Typography>
      )}
      {rootChildren.length > 0 && displayedNodes.length > 0 && (
        <StructureHierarchyTable
          nodes={displayedNodes}
          totalsById={totalsById}
          activeNodeId={activeNodeId}
          canViewStructure={canViewStructure}
        />
      )}
    </Box>
  );
};
